/**
 * Dashboard da Fase de Tratamento do CONECTA PKU (HNS-1594).
 *
 * Entra na Home pela chave `conecta_pku_tratamento_dashboard` em
 * `configuracoes.graficos_dashboard` do programa.
 *
 * O painel da Fase 1 (`conecta_pku_dashboard`) le a view do Relatorio Semanal, que
 * cobre apenas o Teste de Responsividade. A jornada de tratamento e outra, entao
 * este bloco consulta /dashboards/conecta_pku_tratamento/, que agrega os
 * atendimentos, etapas, exames e agendamentos do proprio programa.
 *
 * Um bloco unico para todos os recortes: vem tudo da mesma consulta.
 */
import React, { Component } from "react";
import { Card, Row, Col, Spinner, Alert, Form, Button, Table } from "react-bootstrap";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import { FaFilter, FaEraser } from "react-icons/fa";
import api from "../../../services/api";
import { getCurrentUser, tiposAcesso } from "../../../common/utils";

//Mesma regra do painel da Fase 1: operacao, gestao interna, industria e admin.
//Prestador/laboratorio/psicologa nao entram - veem apenas os seus atendimentos.
const podeVerDashboard = () => {
    const usuario = getCurrentUser();
    if (usuario['is_superuser']) return true;

    const tipo = usuario['tipo_usuario'];
    if (tipo === '' || tipo === 'operador') return true;

    if (tipo === 'gestor') {
        const acessos = tiposAcesso() || {};
        const tipoGestor = acessos.gestor?.tipo_gestor;
        return tipoGestor === 'interno' || tipoGestor === 'industria';
    }

    return false;
};

const COR_PRINCIPAL = '#00ADD8';
const COR_APOIO = '#333159';
const COR_ALERTA = '#a41c12';
const CINZA = '#6b7a8d';

const formataNumero = (valor) => (valor === null || valor === undefined ? '-' : String(valor));

class ConectaPkuTratamentoDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            dados: null,
            carregando: true,
            erro: '',
            data_de: '',
            data_ate: '',
        };
    }

    componentDidMount() {
        this.carregar();
    }

    getProgramaId = () => {
        const usuario = getCurrentUser();
        return this.props.programaId || usuario['ambiente_programa_id'] || null;
    };

    carregar = async () => {
        const programa_id = this.getProgramaId();

        if (!programa_id) {
            this.setState({ carregando: false, erro: 'Selecione um programa para ver os indicadores.' });
            return;
        }

        this.setState({ carregando: true, erro: '' });

        try {
            const params = { programa_id };
            if (this.state.data_de) params.data_de = this.state.data_de;
            if (this.state.data_ate) params.data_ate = this.state.data_ate;

            const { data } = await api.get('/dashboards/conecta_pku_tratamento/', { params });
            this.setState({ dados: data, carregando: false });
        } catch (e) {
            console.error('Erro ao carregar o dashboard da Fase de Tratamento:', e);
            this.setState({
                carregando: false,
                erro: e?.response?.data?.error || 'Não foi possível carregar os indicadores.',
            });
        }
    };

    limpar = () => this.setState({ data_de: '', data_ate: '' }, this.carregar);

    renderIndicador = (titulo, valor, complemento, cor = COR_APOIO) => (
        <Col xs={6} md={4} xl={2} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="py-3">
                    <div style={{ fontSize: '11px', color: CINZA, textTransform: 'uppercase', letterSpacing: '.03em' }}>
                        {titulo}
                    </div>
                    <div style={{
                        fontSize: String(valor).length > 9 ? '19px' : '26px',
                        fontWeight: 700, color: cor, lineHeight: 1.25, whiteSpace: 'nowrap'
                    }}>{valor}</div>
                    {complemento && <div style={{ fontSize: '11px', color: CINZA }}>{complemento}</div>}
                </Card.Body>
            </Card>
        </Col>
    );

    renderCard = (titulo, subtitulo, conteudo, altura = 320, largura = 6) => (
        <Col lg={largura} className="mb-4">
            <Card className="h-100 border-0 shadow-sm">
                <Card.Header className="bg-white border-0 pb-0">
                    <div className="fw-bold">{titulo}</div>
                    {subtitulo && <small className="text-muted">{subtitulo}</small>}
                </Card.Header>
                <Card.Body style={{ height: `${altura}px` }}>{conteudo}</Card.Body>
            </Card>
        </Col>
    );

    semDados = (mensagem = 'Sem registros no período') => (
        <div className="d-flex align-items-center justify-content-center h-100">
            <span className="text-muted" style={{ fontSize: '13px' }}>{mensagem}</span>
        </div>
    );

    render() {
        const { dados, carregando, erro, data_de, data_ate } = this.state;

        if (!podeVerDashboard()) return null;
        if (carregando) return <div className="text-center py-5"><Spinner animation="border" /></div>;
        if (erro) return <Alert variant="warning" className="m-4">{erro}</Alert>;

        const resumo = dados?.resumo || {};
        const etapaAtual = (dados?.etapa_atual || []).filter(e => e.value > 0);
        const progresso = dados?.progresso_etapas || [];
        const exames = (dados?.exames || []).filter(e => e.value > 0);
        const dosagem = (dados?.dosagem_phe || []).filter(d => d.value > 0);
        const evolucao = dados?.evolucao_mensal || [];

        //etapa com mais registros e menos conclusoes: onde a jornada trava
        const gargalo = progresso.length
            ? progresso.reduce((pior, atual) => {
                const pendentesAtual = atual.registros - atual.concluidas;
                const pendentesPior = pior.registros - pior.concluidas;
                return pendentesAtual > pendentesPior ? atual : pior;
            })
            : null;

        return (
            <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                        <h5 className="fw-bold m-0">CONECTA PKU — jornada de tratamento</h5>
                        <small className="text-muted">Indicadores consolidados do programa</small>
                    </div>
                </div>

                {/* filtros */}
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="py-3">
                        <Row className="align-items-end g-2">
                            <Col sm={6} lg={3}>
                                <Form.Label style={{ fontSize: '12px', color: CINZA }}>Data de</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={data_de}
                                    onChange={(e) => this.setState({ data_de: e.target.value })}
                                />
                            </Col>
                            <Col sm={6} lg={3}>
                                <Form.Label style={{ fontSize: '12px', color: CINZA }}>Data até</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={data_ate}
                                    onChange={(e) => this.setState({ data_ate: e.target.value })}
                                />
                            </Col>
                            <Col sm={12} lg={6} className="d-flex gap-2">
                                <Button variant="primary" size="sm" onClick={this.carregar}>
                                    <FaFilter className="me-1" /> Filtrar
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={this.limpar}>
                                    <FaEraser className="me-1" /> Limpar
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* indicadores */}
                <Row>
                    {this.renderIndicador('Pacientes', formataNumero(resumo.pacientes), 'no período')}
                    {this.renderIndicador('Em tratamento', formataNumero(resumo.em_tratamento),
                        'jornada em andamento', COR_PRINCIPAL)}
                    {this.renderIndicador('Ciclos concluídos', formataNumero(resumo.ciclos_concluidos),
                        'validações de ciclo')}
                    {this.renderIndicador('Visitas da nutricionista', formataNumero(resumo.visitas_nutricionista),
                        'etapas concluídas')}
                    {this.renderIndicador('Exames realizados', formataNumero(resumo.exames_realizados),
                        'fenilalanina')}
                    {this.renderIndicador('Dosagem média', resumo.dosagem_media ?? '-',
                        'Phe informada nos exames', COR_ALERTA)}
                </Row>

                <Row>
                    {/* onde os pacientes estao agora */}
                    {this.renderCard(
                        'Pacientes por etapa atual',
                        'Onde a jornada de cada paciente ativo parou',
                        etapaAtual.length ? (
                            /* Barra horizontal, e nao pizza: os nomes das etapas da Fase 2 sao
                               longos ("Etapa Pre Tratamento - Acompanhamento Nutricional") e na
                               pizza eles saiam cortados, com a legenda sobreposta. Mesmo padrao
                               do "Pacientes por status" da Fase 1. */
                            <ResponsiveBar
                                data={[...etapaAtual].reverse()}
                                keys={['value']}
                                indexBy="label"
                                layout="horizontal"
                                margin={{ top: 10, right: 24, bottom: 30, left: 200 }}
                                padding={0.35}
                                colors={[COR_PRINCIPAL]}
                                labelTextColor="#fff"
                                axisBottom={{ tickSize: 0, tickPadding: 8 }}
                                /* nome longo e cortado no eixo por mais margem que se de; o nome
                                   inteiro fica no tooltip */
                                axisLeft={{ tickSize: 0, tickPadding: 8, format: (v) => (String(v).length > 30 ? String(v).slice(0, 29) + '…' : v) }}
                                enableGridX
                                enableGridY={false}
                                tooltip={({ indexValue, value }) => (
                                    <div style={{ background: '#fff', padding: '6px 10px', border: '1px solid #e6ecf3', borderRadius: '4px', fontSize: '12px' }}>
                                        <strong>{indexValue}</strong>: {value}
                                    </div>
                                )}
                            />
                        ) : this.semDados(),
                        360, 6
                    )}

                    {/* exames por status */}
                    {this.renderCard(
                        'Exames de fenilalanina',
                        'Situação dos exames solicitados no período',
                        exames.length ? (
                            <ResponsivePie
                                data={exames}
                                margin={{ top: 20, right: 20, bottom: 60, left: 20 }}
                                innerRadius={0.55}
                                padAngle={1}
                                cornerRadius={3}
                                colors={{ scheme: 'blue_green' }}
                                borderWidth={1}
                                borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
                                arcLinkLabelsSkipAngle={12}
                                arcLinkLabelsTextColor={CINZA}
                                arcLabelsSkipAngle={14}
                                legends={[{
                                    anchor: 'bottom', direction: 'row', translateY: 50,
                                    itemWidth: 110, itemHeight: 16, symbolSize: 10, itemsSpacing: 2,
                                    itemTextColor: CINZA,
                                }]}
                            />
                        ) : this.semDados()
                    )}
                </Row>

                <Row>
                    {/* progresso por etapa: registros x conclusoes */}
                    {this.renderCard(
                        'Progresso por etapa da jornada',
                        gargalo
                            ? `Maior acúmulo de pendências: ${gargalo.etapa}`
                            : 'Registros e conclusões de cada etapa',
                        progresso.length ? (
                            <div style={{ height: '100%', overflowY: 'auto' }}>
                                <Table size="sm" className="mb-0" style={{ fontSize: '12.5px' }}>
                                    <thead>
                                        <tr>
                                            <th>Etapa</th>
                                            <th className="text-center">Registros</th>
                                            <th className="text-center">Concluídas</th>
                                            <th className="text-center">Pendentes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {progresso.map((linha) => {
                                            const pendentes = linha.registros - linha.concluidas;
                                            return (
                                                <tr key={`${linha.ordem}-${linha.etapa}`}>
                                                    <td>{linha.etapa}</td>
                                                    <td className="text-center">{linha.registros}</td>
                                                    <td className="text-center">{linha.concluidas}</td>
                                                    <td className="text-center"
                                                        style={{ color: pendentes > 0 ? COR_ALERTA : CINZA, fontWeight: pendentes > 0 ? 600 : 400 }}>
                                                        {pendentes}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        ) : this.semDados()
                    )}

                    {/* faixas de dosagem */}
                    {this.renderCard(
                        'Dosagem de fenilalanina',
                        'Distribuição dos resultados informados na realização do exame',
                        dosagem.length ? (
                            <ResponsiveBar
                                data={dosagem}
                                keys={['value']}
                                indexBy="label"
                                margin={{ top: 20, right: 20, bottom: 55, left: 45 }}
                                padding={0.35}
                                colors={[COR_PRINCIPAL]}
                                borderRadius={3}
                                axisBottom={{ tickRotation: -25 }}
                                axisLeft={{ tickValues: 4 }}
                                labelSkipHeight={14}
                                enableGridX={false}
                                tooltip={({ data }) => (
                                    <div style={{ background: '#fff', padding: '6px 10px', border: `1px solid ${CINZA}`, fontSize: '12px' }}>
                                        <strong>{data.label}</strong> — {data.value} exame(s)
                                    </div>
                                )}
                            />
                        ) : this.semDados('Nenhuma dosagem informada no período')
                    )}
                </Row>

                <Row>
                    {/* evolucao mensal */}
                    {this.renderCard(
                        'Novos pacientes por mês',
                        'Cadastros no programa ao longo do tempo',
                        evolucao.length ? (
                            <ResponsiveBar
                                data={evolucao}
                                keys={['cadastros']}
                                indexBy="mes"
                                margin={{ top: 20, right: 20, bottom: 55, left: 45 }}
                                padding={0.35}
                                colors={[COR_APOIO]}
                                borderRadius={3}
                                axisBottom={{ tickRotation: -35 }}
                                axisLeft={{ tickValues: 4 }}
                                labelSkipHeight={14}
                                enableGridX={false}
                            />
                        ) : this.semDados(),
                        320, 12
                    )}
                </Row>
            </div>
        );
    }
}

export default ConectaPkuTratamentoDashboard;
